import { getUsersPage, getUsersPerPage, getUsersSort, getUsersSortDirection } from "../selectors";
import { select } from "redux-saga/effects";
import SecureRequest from "../utils/securerequest";
/** @typedef {import("api/typesDef").User} User */
/** @typedef {import("api/types/users").Task} Task */
/** @typedef {import("api/types/users").Announcement} Announcement */

export function* getUserList() {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  const page = yield select(getUsersPage);
  const size = yield select(getUsersPerPage);
  const sort = yield select(getUsersSort);
  const direction = yield select(getUsersSortDirection);
  const params = {
    page,
    size,
    sort,
    direction,
  };

  return yield Request.get(`/users`, { params });
}

export function* createUser(params) {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return yield Request.post("/users/admin", params);
}

export function* getUserDeactivationReasons() {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return yield Request.get("users/deactivation/reasons");
}

export const searchUsers = async (userId, params) => {
  const url = `/v2/users/${userId}/search-users`;

  const sr = new SecureRequest();
  const Request = sr.getRequest();

  return await Request.get(url, { params });
};

/**
 * This function retrieves user data for a specific user ID.
 * @param {number|string} userId - The `userId` parameter is the unique identifier of
 * the user whose information you want to retrieve.
 * @returns {Promise<{data: User}>} The result of a GET request to the endpoint `/v3/users/[userId]` .
 */
export const getUser = async (userId) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get(`/v3/users/${userId}`, {});
};

/**
 * This function retrieves users data.
 * @param {Object} params - Object filters for the users.
 * @returns {Promise<{data: User[]}>} The result of a GET request to the endpoint `/v3/users` .
 */
export const getUsers = async (params) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get("/v3/users", { params });
};

export const getFamilyNavigators = async () => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get("/v3/users/family-navigators");
};

/**
 * This function retrieves user tasks.
 * @param {number|string} userId - The `userId` parameter is the unique identifier of
 * the user whose tasks you want to retrieve.
 * @param {?Object} params - Object filters for the tasks.
 * @param {string} params.type - The type of the task: `image_upload`, `video_call_signature`, `treatment_plan_signature`,
 * `fillout_iframe` or `other_consent_forms`.
 * @param {string} params.completed - The completion status of the task: `true`, `false`
 * @param {string} params.title - The title of the task
 * @returns {Promise<{data: Task[]}>} The result of a GET request to the endpoint `/v3/users/[userId]/tasks` .
 */
export const getUserTasks = async (userId, params) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get(`/v3/users/${userId}/tasks`, { params });
};

/**
 * This function fetches upcoming tasks for a specific user ID.
 * @param {number|string} userId - The unique identifier of the user.
 * @param {Object} [params] - Optional query parameters to filter results.
 * @returns {Promise<{data: import("api/types/users").UpcomingTask[]}>} The result of a GET request to the endpoint `/v3/users/[userId]/upcoming-tasks`.
 * */
export const getUserUpcomingTasks = async (userId, params) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get(`/v3/users/${userId}/upcoming-tasks`, { params });
};

/**
 * This function creates a new task for a specific user ID.
 * @param {number|string} userId - The `userId` parameter is the unique identifier of
 * the user for whom to create the task.
 * @param {Object} taskData - The task data object containing userId, type, taskName, metadata, etc.
 * @returns {Promise<{data: Object}>} The result of a POST request to the endpoint `/v3/users/[userId]/tasks`.
 */
export const createUserTask = async (userId, taskData) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.post(`/v3/users/${userId}/tasks`, taskData);
};

/**
 * This function updates an existing task for a specific user ID.
 * @param {number|string} userId - The `userId` parameter is the unique identifier of
 * the user who owns the task.
 * @param {number|string} taskId - The `taskId` parameter is the unique identifier of
 * the task to update.
 * @param {Object} taskData - The task data object containing fields to update.
 * @returns {Promise<{data: Object}>} The result of a PUT request to the endpoint `/v3/users/tasks/[taskId]`.
 */
export const updateUserTask = async (userId, taskId, taskData) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.put(`/v3/users/tasks/${taskId}`, taskData);
};

/**
 * This function retrieves user announcements.
 * @param {number|string} userId - The `userId` parameter is the unique identifier of
 * the user whose announcements you want to retrieve.
 * @returns {Promise<{data: Announcement[]}>} The result of a GET request to the endpoint `/v3/users/[userId]/announcements` .
 */
export const getUserAnnouncements = async (userId) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get(`/v3/users/${userId}/announcements`);
};

/**
 * This function marks an announcement as read for a specific user ID.
 * @param {Object} params - The parameters object containing the userId, announcementId, and metadata.
 * @param {number|string} params.userId - The `userId` parameter is the unique identifier of
 * the user whose announcement you want to mark as read.
 * @param {number|string} params.announcementId - The `announcementId` parameter is the unique identifier of
 * the announcement to mark as read.
 * @param {Object} params.metadata - The metadata object containing the fields to mark as read.
 * @returns {Promise<void>} The result of a POST request to the endpoint `/v3/users/[userId]/announcements/[announcementId]` .
 */
export const markAnnouncementAsRead = async ({ userId, announcementId, metadata }) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.post(`/v3/users/${userId}/announcements/${announcementId}`, metadata);
};
